import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "../styles/MenuInsights.css";

const MenuInsightsZomato = ({ selectedRestData }) => {
  // ✅ Guard: no data at all
  if (!selectedRestData || selectedRestData.length === 0) {
    return null;
  }

  // ✅ Pick latest month WITH data (skip empty months)
  const top = selectedRestData
    .slice()
    .sort((a, b) => new Date(b.period) - new Date(a.period))
    .find(
      (item) =>
        item?.top_5_dishes?.length > 0 ||
        item?.bottom_5_dishes?.length > 0 ||
        item?.top_3_promos?.length > 0
    );

  // ✅ If no valid data found at all
  if (!top) {
    return <div className="menu-wrapper">No menu insights available</div>;
  }

  // ✅ Safe data extraction
  const dishes = top?.top_5_dishes || [];
  const worst5 = top?.bottom_5_dishes || [];
  const promoData = top?.top_3_promos || [];

  console.log("top",selectedRestData)
  console.log("promoData",promoData )

  // ✅ Sort top dishes
  const sorted = [...dishes].sort(
    (a, b) => (b.ITEM_SUBTOTAL || 0) - (a.ITEM_SUBTOTAL || 0)
  );
  const top5 = sorted.slice(0, 5);

  // ✅ GMV calculations
  const totalGMV = top?.gmv || 0;

  const top5GMV = top5.reduce(
    (sum, d) => sum + (d.ITEM_SUBTOTAL || 0),
    0
  );

  const contributionData =
    totalGMV > 0
      ? [
          {
            name: "Top 5",
            value: Math.round((top5GMV / totalGMV) * 100),
          },
          {
            name: "Others",
            value:
              100 - Math.round((top5GMV / totalGMV) * 100),
          },
        ]
      : [];

  // ✅ Format month
  const formatPeriod = (period) => {
    if (!period) return "-";
    const [year, month] = period.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="menu-wrapper">
      <div className="flex justify-between items-center mb-2">
        <h2 className="section-title">Menu Insights</h2>
        <div className="text-lg font-semibold text-gray-700">
          Month: {formatPeriod(top.period)}
        </div>
      </div>

      <div className="charts-grid">
        {/* TOP TABLE */}
        <div className="charts-cards green">
          <h4>Top 5 Dishes</h4>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Dish</th>
                  <th>Orders</th>
                  <th>GMV</th>
                </tr>
              </thead>
              <tbody>
                {top5.length > 0 ? (
                  top5.map((item, i) => (
                    <tr key={i}>
                      <td>{item.ITEM_NAME}</td>
                      <td>{item.ORDERS}</td>
                      <td>₹{item.ITEM_SUBTOTAL}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">No data</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* WORST TABLE */}
        <div className="charts-cards red">
          <h4>Worst 5 Dishes</h4>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Dish</th>
                  <th>Orders</th>
                  <th>GMV</th>
                </tr>
              </thead>
              <tbody>
                {worst5.length > 0 ? (
                  worst5.map((item, i) => (
                    <tr key={i}>
                      <td>{item.ITEM_NAME}</td>
                      <td>{item.ORDERS}</td>
                      <td>₹{item.ITEM_SUBTOTAL}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">No data</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PROMO CHART */}
        <div className="charts-cards blue">
          <h4>Top Promo Codes</h4>
          <div className="chart-content">
            {promoData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={promoData}>
                  <XAxis type="number" stroke="#ccc" />
                  <YAxis
                    dataKey="PROMO_CODE"
                    type="category"
                    width={100}
                    stroke="#ccc"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#111827",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 8px",
                      fontSize: "12px",
                      color: "#fff",
                    }}
                    itemStyle={{ color: "#fff" }}
                    cursor={{ fill: "transparent" }}
                    isAnimationActive={false}
                    wrapperStyle={{ pointerEvents: "none" }}
                  />
                  <Bar
                    dataKey="PROMO_BURN"
                    name="PROMO BURN"
                    fill="#3b82f6"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">No promo data</div>
            )}
          </div>
        </div>

        {/* CONTRIBUTION */}
        <div className="charts-cards green">
          <h4>Revenue Contribution</h4>
          <div className="chart-content">
            {contributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={contributionData}>
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    stroke="#ccc"
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    stroke="#ccc"
                  />
                  <Tooltip
                    formatter={(v) => `${v}%`}
                    contentStyle={{
                      backgroundColor: "#111827",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 8px",
                      fontSize: "12px",
                      color: "#fff",
                    }}
                    itemStyle={{ color: "#fff" }}
                    cursor={{ fill: "transparent" }}
                    isAnimationActive={false}
                    wrapperStyle={{ pointerEvents: "none" }}
                  />
                  <Bar dataKey="value" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">No contribution data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuInsightsZomato;